import { AiError } from './types'

/**
 * Traduce un error de IA a un mensaje amigable y accionable para el
 * usuario, en vez de mostrar el volcado crudo del proveedor (que suele
 * ser técnico y revelar datos como la clave enviada).
 *
 * Detecta patrones comunes en las respuestas de OpenAI/Anthropic y los
 * sustituye por indicaciones claras. Si no reconoce el patrón, devuelve
 * el mensaje original.
 *
 * @param err error capturado (típicamente un AiError del provider).
 * @param t función de traducción i18n para las claves `ai.error.*`.
 * @returns texto a mostrar al usuario.
 */
export function humanizeAiError(
  err: unknown,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  const raw = err instanceof Error ? err.message : String(err)
  const lower = raw.toLowerCase()

  // Clave inválida / no autorizada (401). El mensaje crudo incluye la
  // clave enviada ("Incorrect API key provided: Enkil@1981..."), que no
  // queremos mostrar.
  if (
    (err instanceof AiError && err.status === 401) ||
    lower.includes('incorrect api key') ||
    lower.includes('invalid api key') ||
    lower.includes('invalid x-api-key') ||
    (lower.includes('authentication') && lower.includes('invalid'))
  ) {
    return t('ai.error.invalidKey')
  }

  // Sin saldo / cuota agotada (429).
  if (
    (err instanceof AiError && err.status === 429) ||
    lower.includes('quota') ||
    lower.includes('billing') ||
    lower.includes('rate limit') ||
    lower.includes('insufficient_quota')
  ) {
    return t('ai.error.quota')
  }

  // Clave con formato incorrecto capturada por requireApiKey().
  if (lower.includes('no tiene un formato válido') || lower.includes('does not have a valid format')) {
    return t('ai.error.invalidKey')
  }

  // Falta la clave (no configurada).
  if (lower.includes('falta la api key') || lower.includes('api key is missing')) {
    return t('ai.error.missingKey')
  }

  // Modelo no disponible / no encontrado.
  if (lower.includes('model not found') || lower.includes('does not exist') || lower.includes('invalid model')) {
    return t('ai.error.modelNotFound')
  }

  // Error de red (no se pudo contactar con el endpoint).
  if (lower.includes('failed to fetch') || lower.includes('network') || lower.includes('networkerror')) {
    return t('ai.error.network')
  }

  return raw
}
